import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import moment from "moment";
import Supercluster from "supercluster";
import { apiV2 } from "../../../api/axiosConfig";
import { useMapSettings } from "../../operational-map/context/useMapSettings";
import type { UserLocationType } from "../../operational-map/context/MapSettingsContext";

// --- Types ---

export type UserLocation = {
    _id: string;
    clientID: string;
    clientName: string;
    departmentID: string;
    departmentName: string;
    email: string;
    firstName: string;
    middleNames: string;
    lastName: string;
    coordinates: number[];
    receivedAt: string;
    source: string;
    phoneNumbers: { number: string }[];
    mobilePhone?: { e164: string } | null;
    personalMobilePhone?: { e164: string } | null;
    homePhone?: { e164: string } | null;
};

export type UserLocationPopupUser = {
    id: string;
    name: string;
    firstName: string;
    middleNames: string;
    lastName: string;
    email: string;
    clientName: string;
    departmentID: string;
    departmentName: string;
    source: string;
    phones: { label: string; number: string }[];
    lat: number;
    lng: number;
    receivedAt: string;
    worldSearchUrl: string;
};

export type UserLocationPopupState = {
    visible: boolean;
    position: { x: number; y: number };
    users: UserLocationPopupUser[];
};

// --- WebGL helpers ---

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) ?? "Shader compile error");
    }
    return shader;
}

function buildProgram(gl: WebGLRenderingContext, vert: string, frag: string): WebGLProgram {
    const vs = compileShader(gl, gl.VERTEX_SHADER, vert);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, frag);
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    return prog;
}

function loadTexture(gl: WebGLRenderingContext, src: string, onLoad?: () => void): WebGLTexture {
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
    const img = new Image();
    img.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 1);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0);
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        // Bias toward sharper mip levels — point sprites don't have natural LOD gradients
        // so the GPU tends to pick too blurry a level without this hint
        const ext = gl.getExtension("EXT_texture_filter_anisotropic")
                 ?? gl.getExtension("MOZ_EXT_texture_filter_anisotropic")
                 ?? gl.getExtension("WEBKIT_EXT_texture_filter_anisotropic");
        if (ext) gl.texParameterf(gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, 4);
        onLoad?.();
    };
    img.src = src;
    return tex;
}

// --- Shaders ---

const ICON_VERT = `
    attribute vec2 a_position;
    attribute float a_highlighted;
    attribute vec3 a_color;
    varying vec3 v_color;
    void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        gl_PointSize = a_highlighted > 0.5 ? 32.0 : 24.0;
        v_color = a_color;
    }
`;

// Point size is 28px normal / 38px highlighted.
// Layers from back to front: green circle fill → icon (scaled to 65% of sprite).
// Use EXT_shader_texture_lod if available to explicitly sample mip 0,
// since WebGL can't compute LOD gradients for gl_PointCoord automatically.
const ICON_FRAG = `
    #extension GL_EXT_shader_texture_lod : enable
    precision mediump float;
    uniform sampler2D u_texture;
    varying vec3 v_color;
    void main() {
        vec2  uv   = gl_PointCoord - 0.5;
        float dist = length(uv);

        float sm        = 0.04;
        float fillR     = 0.5;
        float iconScale = 0.71;

        // Discard outside circular boundary
        if (dist > fillR) discard;

        // Filled circle, antialiased edge, color from vertex attribute
        float fillAlpha = smoothstep(fillR, fillR - sm, dist);
        vec4 col = vec4(v_color, fillAlpha);

        // Icon sampled from scaled-down UV region
        // Use texture2DLodEXT when available for explicit sharp mip selection,
        // fall back to texture2D with bias toward sharper level
        vec2 iconUV = (gl_PointCoord - 0.5) / iconScale + 0.5;
        if (iconUV.x >= 0.0 && iconUV.x <= 1.0 && iconUV.y >= 0.0 && iconUV.y <= 1.0) {
            #ifdef GL_EXT_shader_texture_lod
                vec4 iconColor = texture2DLodEXT(u_texture, iconUV, 0.0);
            #else
                vec4 iconColor = texture2D(u_texture, iconUV, -1.0);
            #endif
            // Texture is premultiplied — RGB already scaled by alpha
            col.rgb = iconColor.rgb + col.rgb * (1.0 - iconColor.a);
        }

        if (col.a < 0.01) discard;
        // Premultiply RGB by alpha for correct edge blending
        gl_FragColor = vec4(col.rgb * col.a, col.a);
    }
`;

// --- Icon paths per source type ---

const DEFAULT_ICON  = "/assets/mapicons/user_transparent.png";
const DEFAULT_COLOR: [number, number, number] = [0.0, 0.804, 0.4];  // #00cd66 green

const ICON_OVERRIDES: Record<string, string> = {
    satellite: "/assets/mapicons/satellite_transparent.png",
};
const COLOR_OVERRIDES: Record<string, [number, number, number]> = {
    satellite: [0.945, 0.247, 0.447],  // #f13f72 pink
};

const CLICK_RADIUS = 30;
const CLUSTER_RADIUS = 50;
const CLUSTER_MAX_ZOOM = 14;

// --- Cluster colours ---
const CLUSTER_COLORS: Record<string, string> = {
    satellite: "#f13f72",
    default:   "#00cd66",
};
const CLUSTER_TEXT = "#ffffff";

// --- Rendered point for hit testing ---

type RenderedPoint = {
    x: number;
    y: number;
    user?: UserLocation;
    isCluster: boolean;
    clusterID?: number;
    count?: number;
    clusterGroup?: string;
};

// --- Overlay factory ---

function createUserLocationsOverlay(
    map: google.maps.Map,
    onRenderedPoints: (pts: RenderedPoint[]) => void,
    onRedraw: () => void,
) {
    // WebGL canvas for individual user icons
    const glCanvas = document.createElement("canvas");
    glCanvas.style.position = "absolute";
    glCanvas.style.top = "0";
    glCanvas.style.left = "0";
    glCanvas.style.pointerEvents = "none";
    map.getDiv().appendChild(glCanvas);

    // 2D canvas for cluster bubbles (drawn on top)
    const clusterCanvas = document.createElement("canvas");
    clusterCanvas.style.position = "absolute";
    clusterCanvas.style.top = "0";
    clusterCanvas.style.left = "0";
    clusterCanvas.style.pointerEvents = "none";
    map.getDiv().appendChild(clusterCanvas);

    const gl = glCanvas.getContext("webgl", { alpha: true, premultipliedAlpha: true })!;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const prog = buildProgram(gl, ICON_VERT, ICON_FRAG);

    const textures: Record<string, WebGLTexture> = {};
    textures["default"] = loadTexture(gl, DEFAULT_ICON, onRedraw);
    for (const [key, src] of Object.entries(ICON_OVERRIDES)) {
        textures[key] = loadTexture(gl, src, onRedraw);
    }

    let isReady = false;
    const overlay = new (class extends google.maps.OverlayView {
        onAdd()    { isReady = true; }
        draw()     {}
        onRemove() { isReady = false; }
        getProjectionSafe(): google.maps.MapCanvasProjection | null {
            if (!isReady) return null;
            try { return this.getProjection(); } catch { return null; }
        }
    })();
    overlay.setMap(map);

    // Points supplied externally (already clustered)
    type DrawPoint = { lng: number; lat: number; user?: UserLocation; isCluster: boolean; clusterID?: number; count?: number; clusterGroup?: string };
    let drawPoints: DrawPoint[] = [];
    let hoveredIDs = new Set<string>();
    let rafId: number | null = null;

    function resizeCanvases(width: number, height: number, dpr: number) {
        for (const c of [glCanvas, clusterCanvas]) {
            c.style.width  = `${width}px`;
            c.style.height = `${height}px`;
            c.width  = width  * dpr;
            c.height = height * dpr;
        }
    }

    function draw() {
        const projection = overlay.getProjectionSafe();
        if (!projection) return;

        const mapDiv = map.getDiv() as HTMLElement;
        const dpr    = window.devicePixelRatio || 1;
        const width  = mapDiv.offsetWidth;
        const height = mapDiv.offsetHeight;
        if (!width || !height) return;

        resizeCanvases(width, height, dpr);

        gl.viewport(0, 0, glCanvas.width, glCanvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        const ctx2d = clusterCanvas.getContext("2d")!;
        ctx2d.clearRect(0, 0, clusterCanvas.width, clusterCanvas.height);
        ctx2d.scale(dpr, dpr);

        const allPts: RenderedPoint[] = [];

        if (drawPoints.length === 0) {
            ctx2d.setTransform(1, 0, 0, 1, 0, 0);
            onRenderedPoints(allPts);
            return;
        }

        // Split into individual points and clusters
        const individuals: DrawPoint[] = [];
        const clusters: DrawPoint[] = [];
        for (const dp of drawPoints) {
            if (dp.isCluster) clusters.push(dp);
            else individuals.push(dp);
        }

        // --- Draw individual icons via WebGL ---
        const byIcon: Record<string, { positions: number[]; highlighted: number[]; colors: number[]; pts: RenderedPoint[] }> = {};

        for (const dp of individuals) {
            if (!isFinite(dp.lng) || !isFinite(dp.lat)) continue;
            const pixel = projection.fromLatLngToContainerPixel(new google.maps.LatLng(dp.lat, dp.lng));
            if (!pixel) continue;

            const iconKey = dp.user && (dp.user.source in ICON_OVERRIDES) ? dp.user.source : "default";
            if (!byIcon[iconKey]) byIcon[iconKey] = { positions: [], highlighted: [], colors: [], pts: [] };

            const nx = (pixel.x / width) * 2 - 1;
            const ny = 1 - (pixel.y / height) * 2;
            const color = COLOR_OVERRIDES[iconKey] ?? DEFAULT_COLOR;

            byIcon[iconKey].positions.push(nx, ny);
            byIcon[iconKey].highlighted.push(dp.user && hoveredIDs.has(dp.user._id) ? 1.0 : 0.0);
            byIcon[iconKey].colors.push(...color);
            byIcon[iconKey].pts.push({ x: pixel.x, y: pixel.y, user: dp.user, isCluster: false });
        }

        gl.useProgram(prog);
        const aPosLoc   = gl.getAttribLocation(prog, "a_position");
        const aHlLoc    = gl.getAttribLocation(prog, "a_highlighted");
        const aColorLoc = gl.getAttribLocation(prog, "a_color");

        for (const [iconKey, group] of Object.entries(byIcon)) {
            if (group.positions.length === 0) continue;

            const tex = textures[iconKey] ?? textures["default"];
            const count = group.positions.length / 2;

            const posBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(group.positions), gl.DYNAMIC_DRAW);
            gl.enableVertexAttribArray(aPosLoc);
            gl.vertexAttribPointer(aPosLoc, 2, gl.FLOAT, false, 0, 0);

            const hlBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, hlBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(group.highlighted), gl.DYNAMIC_DRAW);
            gl.enableVertexAttribArray(aHlLoc);
            gl.vertexAttribPointer(aHlLoc, 1, gl.FLOAT, false, 0, 0);

            const colorBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, colorBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(group.colors), gl.DYNAMIC_DRAW);
            gl.enableVertexAttribArray(aColorLoc);
            gl.vertexAttribPointer(aColorLoc, 3, gl.FLOAT, false, 0, 0);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.uniform1i(gl.getUniformLocation(prog, "u_texture"), 0);

            gl.drawArrays(gl.POINTS, 0, count);
            allPts.push(...group.pts);
        }

        // --- Draw cluster bubbles via 2D canvas ---
        for (const dp of clusters) {
            if (!isFinite(dp.lng) || !isFinite(dp.lat)) continue;
            const pixel = projection.fromLatLngToContainerPixel(new google.maps.LatLng(dp.lat, dp.lng));
            if (!pixel) continue;

            const hovered = dp.clusterID !== undefined && hoveredIDs.has(`cluster_${dp.clusterID}`);
            const r = hovered ? 17 : 12;
            ctx2d.beginPath();
            ctx2d.arc(pixel.x, pixel.y, r, 0, Math.PI * 2);
            ctx2d.fillStyle = CLUSTER_COLORS[dp.clusterGroup ?? "default"] ?? CLUSTER_COLORS["default"];
            ctx2d.fill();

            ctx2d.fillStyle = CLUSTER_TEXT;
            ctx2d.font = `bold ${hovered ? 13 : 10}px sans-serif`;
            ctx2d.textAlign = "center";
            ctx2d.textBaseline = "middle";
            ctx2d.fillText(String(dp.count ?? 0), pixel.x, pixel.y + 1);

            allPts.push({ x: pixel.x, y: pixel.y, isCluster: true, clusterID: dp.clusterID, count: dp.count, clusterGroup: dp.clusterGroup });
        }

        ctx2d.setTransform(1, 0, 0, 1, 0, 0);
        onRenderedPoints(allPts);
    }

    function startLoop() {
        if (rafId !== null) return;
        const loop = () => {
            draw();
            rafId = drawPoints.length > 0 ? requestAnimationFrame(loop) : null;
        };
        rafId = requestAnimationFrame(loop);
    }

    const mapListeners = [
        google.maps.event.addListener(map, "idle",           draw),
        google.maps.event.addListener(map, "bounds_changed", draw),
    ];

    return {
        setDrawPoints(next: DrawPoint[]) {
            drawPoints = next;
            if (next.length > 0) startLoop();
            else draw();
        },
        setHoveredIDs(ids: Set<string>) {
            hoveredIDs = ids;
            draw();
        },
        destroy() {
            if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
            mapListeners.forEach((l) => google.maps.event.removeListener(l));
            overlay.setMap(null);
            if (glCanvas.parentNode) glCanvas.parentNode.removeChild(glCanvas);
            if (clusterCanvas.parentNode) clusterCanvas.parentNode.removeChild(clusterCanvas);
        },
    };
}

type UserLocationsOverlay = ReturnType<typeof createUserLocationsOverlay>;

// --- Supercluster setup ---

type UserClusterProps = { user: UserLocation };

function buildClusterIndex(users: UserLocation[]): Supercluster<UserClusterProps> {
    const index = new Supercluster<UserClusterProps>({ radius: CLUSTER_RADIUS, maxZoom: CLUSTER_MAX_ZOOM });
    const points: GeoJSON.Feature<GeoJSON.Point, UserClusterProps>[] = users.map((u) => ({
        type: "Feature",
        properties: { user: u },
        geometry: { type: "Point", coordinates: [u.coordinates[0], u.coordinates[1]] },
    }));
    index.load(points);
    return index;
}

// --- Fetch ---

async function fetchUserLocations(userTypes: UserLocationType[], clientID: string | undefined): Promise<UserLocation[]> {
    const res = await apiV2.get("ops/user-locations", {
        params: { sources: userTypes.join(","), clientID: clientID ?? undefined },
    });
    return res.data as UserLocation[];
}

// --- Hook ---

// --- Point-in-polygon (ray casting) ---

function pointInPolygon(lat: number, lng: number, path: google.maps.LatLng[]): boolean {
    let inside = false;
    for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
        const xi = path[i].lng(), yi = path[i].lat();
        const xj = path[j].lng(), yj = path[j].lat();
        if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
            inside = !inside;
        }
    }
    return inside;
}

// Haversine distance in metres
function haversineMetres(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export type UserLocationsLayerResult = {
    popup: UserLocationPopupState;
    setPopup: (s: UserLocationPopupState) => void;
    getHitsAtPoint: (clientX: number, clientY: number) => { users: UserLocation[]; isCluster: boolean; hoveredID: string | null };
    setHoveredIDs: (ids: Set<string>) => void;
    openPopupForUsers: (users: UserLocation[], position: { x: number; y: number }) => void;
    getUsersInPolygon: (path: google.maps.LatLng[]) => UserLocation[];
    getUsersInCircle: (center: google.maps.LatLng, radiusMetres: number) => UserLocation[];
    userCount: number;
    usersLoading: boolean;
    userList: UserLocation[];
};

export function useUserLocationsLayer(map: google.maps.Map | null, autoReload: boolean, userSearch = ""): UserLocationsLayerResult {
    const { settings } = useMapSettings();
    const { userTypes, clientID, clusterUsers, departmentIDs } = settings;

    const overlayRef = useRef<UserLocationsOverlay | null>(null);
    const renderedPointsRef = useRef<RenderedPoint[]>([]);
    const clusterIndexRef = useRef<Supercluster<UserClusterProps> | null>(null);
    const satelliteClusterIndexRef = useRef<Supercluster<UserClusterProps> | null>(null);
    const usersRef = useRef<UserLocation[]>([]);         // full unfiltered list (for area selection)
    const filteredUsersRef = useRef<UserLocation[]>([]); // search-filtered list (for display)
    const clusterUsersRef = useRef(clusterUsers);
    useEffect(() => { clusterUsersRef.current = clusterUsers; }, [clusterUsers]);

    const [popup, setPopup] = useState<UserLocationPopupState>({ visible: false, position: { x: 0, y: 0 }, users: [] });
    const [filteredCount, setFilteredCount] = useState(0);
    const [userList, setUserList] = useState<UserLocation[]>([]);

    const { data, isLoading } = useQuery(
        ["user-locations-layer", userTypes, clientID],
        () => fetchUserLocations(userTypes, clientID),
        { refetchInterval: autoReload ? 60_000 : false, enabled: userTypes.length > 0 }
    );

    // --- Overlay lifecycle ---

    useEffect(() => {
        if (!map) return;

        const overlay = createUserLocationsOverlay(
            map,
            (pts) => { renderedPointsRef.current = pts; },
            () => { pushClusters(map, overlayRef.current, clusterIndexRef.current, satelliteClusterIndexRef.current, filteredUsersRef.current, clusterUsersRef.current); }
        );
        overlayRef.current = overlay;

        // Redraw clusters on zoom/pan
        const listeners = [
            google.maps.event.addListener(map, "idle",           () => pushClusters(map, overlayRef.current, clusterIndexRef.current, satelliteClusterIndexRef.current, filteredUsersRef.current, clusterUsersRef.current)),
            google.maps.event.addListener(map, "bounds_changed", () => pushClusters(map, overlayRef.current, clusterIndexRef.current, satelliteClusterIndexRef.current, filteredUsersRef.current, clusterUsersRef.current)),
        ];

        return () => {
            overlay.destroy();
            overlayRef.current = null;
            renderedPointsRef.current = [];
            listeners.forEach((l) => google.maps.event.removeListener(l));
        };
    }, [map]);

    // Rebuild cluster index when data, search, or clustering setting changes
    useEffect(() => {
        const next = userTypes.length > 0 ? (data ?? []) : [];
        usersRef.current = next;
        const q = userSearch.toLowerCase();
        const byDept = departmentIDs.length > 0 ? next.filter((u) => departmentIDs.includes(u.departmentID)) : next;
        filteredUsersRef.current = q
            ? byDept.filter((u) => `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
            : byDept;
        setFilteredCount(filteredUsersRef.current.length);
        setUserList([...usersRef.current]);
        const defaultFiltered  = filteredUsersRef.current.filter((u) => u.source !== "satellite");
        const satelliteFiltered = filteredUsersRef.current.filter((u) => u.source === "satellite");
        clusterIndexRef.current          = defaultFiltered.length > 0  ? buildClusterIndex(defaultFiltered)  : null;
        satelliteClusterIndexRef.current = satelliteFiltered.length > 0 ? buildClusterIndex(satelliteFiltered) : null;
        pushClusters(map, overlayRef.current, clusterIndexRef.current, satelliteClusterIndexRef.current, filteredUsersRef.current, clusterUsers);
    }, [data, userTypes, clusterUsers, map, userSearch, departmentIDs]);

    const getHitsAtPoint = (clientX: number, clientY: number): { users: UserLocation[]; isCluster: boolean; hoveredID: string | null } => {
        if (!map) return { users: [], isCluster: false, hoveredID: null };
        const mapDiv = map.getDiv() as HTMLElement;
        const rect = mapDiv.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        let closest: { pt: RenderedPoint; dist: number } | null = null;
        for (const pt of renderedPointsRef.current) {
            const dist = Math.hypot(pt.x - x, pt.y - y);
            if (dist <= CLICK_RADIUS && (!closest || dist < closest.dist)) {
                closest = { pt, dist };
            }
        }

        if (!closest) return { users: [], isCluster: false, hoveredID: null };

        if (closest.pt.isCluster && closest.pt.clusterID !== undefined) {
            const idx = closest.pt.clusterGroup === "satellite" ? satelliteClusterIndexRef.current : clusterIndexRef.current;
            if (idx) {
                const leaves = idx.getLeaves(closest.pt.clusterID, Infinity);
                return { users: leaves.map((l) => l.properties.user), isCluster: true, hoveredID: `cluster_${closest.pt.clusterID}` };
            }
        }

        // In non-cluster mode collect all individual points within click radius
        if (!clusterUsersRef.current) {
            const all = renderedPointsRef.current
                .filter((pt) => !pt.isCluster && Math.hypot(pt.x - x, pt.y - y) <= CLICK_RADIUS && pt.user)
                .map((pt) => pt.user!);
            return { users: all, isCluster: false, hoveredID: closest.pt.user?._id ?? null };
        }

        if (closest.pt.user) return { users: [closest.pt.user], isCluster: false, hoveredID: closest.pt.user._id };
        return { users: [], isCluster: false, hoveredID: null };
    };

    const setHoveredIDs = (ids: Set<string>) => {
        overlayRef.current?.setHoveredIDs(ids);
    };

    const openPopupForUsers = (users: UserLocation[], position: { x: number; y: number }) => {
        const popupUsers: UserLocationPopupUser[] = users.map((u) => {
            const phones: { label: string; number: string }[] = [];
            if (u.mobilePhone?.e164) phones.push({ label: "Mobile", number: u.mobilePhone.e164 });
            if (u.personalMobilePhone?.e164) phones.push({ label: "Personal Mobile", number: u.personalMobilePhone.e164 });
            if (u.homePhone?.e164) phones.push({ label: "Home", number: u.homePhone.e164 });
            u.phoneNumbers?.forEach(p => { if (p.number) phones.push({ label: "Phone", number: p.number }); });
            return {
                id: u._id,
                name: `${u.firstName} ${u.lastName}`.trim() || u.email,
                firstName: u.firstName,
                middleNames: u.middleNames ?? "",
                lastName: u.lastName,
                email: u.email,
                clientName: u.clientName,
                departmentID: u.departmentID ?? "",
                departmentName: u.departmentName ?? "",
                source: u.source === "facility" ? "Stationed Facility" : u.source,
                phones,
                lat: u.coordinates[1] ?? 0,
                lng: u.coordinates[0] ?? 0,
                receivedAt: u.receivedAt,
                worldSearchUrl: `world-search?userID=${u._id}&startDate=${moment().subtract(1, "month").format("YYYY-MM-DD")}&endDate=${moment().add(1, "month").format("YYYY-MM-DD")}`,
            };
        });
        popupUsers.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        setPopup({ visible: true, position, users: popupUsers });
    };

    const getUsersInPolygon = (path: google.maps.LatLng[]): UserLocation[] =>
        usersRef.current.filter((u) => pointInPolygon(u.coordinates[1], u.coordinates[0], path));

    const getUsersInCircle = (center: google.maps.LatLng, radiusMetres: number): UserLocation[] =>
        usersRef.current.filter((u) => haversineMetres(center.lat(), center.lng(), u.coordinates[1], u.coordinates[0]) <= radiusMetres);

    const userCount = userTypes.length > 0 ? filteredCount : 0;

    const usersLoading = userTypes.length > 0 && isLoading;

    return { popup, setPopup, getHitsAtPoint, setHoveredIDs, openPopupForUsers, getUsersInPolygon, getUsersInCircle, userCount, usersLoading, userList };
}

// --- Helper: push clustered draw points to overlay ---

function clustersFromIndex(
    index: Supercluster<UserClusterProps>,
    bbox: [number, number, number, number],
    zoom: number,
    group: string,
) {
    return index.getClusters(bbox, zoom).map((c) => {
        const [lng, lat] = c.geometry.coordinates;
        const props = c.properties as any;
        if (props.cluster) {
            return { lng, lat, isCluster: true, clusterID: props.cluster_id as number, count: props.point_count as number, clusterGroup: group };
        }
        return { lng, lat, isCluster: false, user: (props as UserClusterProps).user, clusterGroup: group };
    });
}

function pushClusters(
    map: google.maps.Map | null,
    overlay: UserLocationsOverlay | null,
    defaultIndex: Supercluster<UserClusterProps> | null,
    satelliteIndex: Supercluster<UserClusterProps> | null,
    users: UserLocation[],
    cluster: boolean,
) {
    if (!overlay) return;
    if (!map || users.length === 0) {
        overlay.setDrawPoints([]);
        return;
    }

    if (!cluster) {
        overlay.setDrawPoints(users.map((u) => ({ lng: u.coordinates[0], lat: u.coordinates[1], isCluster: false, user: u })));
        return;
    }

    const zoom   = Math.floor(map.getZoom() ?? 0);
    const bounds = map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const bbox: [number, number, number, number] = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];

    const drawPoints = [];
    if (defaultIndex)   drawPoints.push(...clustersFromIndex(defaultIndex,   bbox, zoom, "default"));
    if (satelliteIndex) drawPoints.push(...clustersFromIndex(satelliteIndex, bbox, zoom, "satellite"));

    overlay.setDrawPoints(drawPoints);
}
